import { Helmet } from 'react-helmet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export default function CookiePolicy() {
  return (
    <div className="container max-w-4xl py-12">
      <Helmet>
        <title>Cookie Policy - Trotting the World</title>
        <meta name="description" content="Learn about the cookies we use and how you can manage your preferences." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <h1 className="text-4xl font-bold mb-6">Cookie Policy</h1>
      <p className="text-muted-foreground mb-8">
        Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </p>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>What Are Cookies?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              Cookies are small text files that are stored on your device when you visit our website. They help us provide you with a better 
              experience by remembering your preferences and understanding how you use our site.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Types of Cookies We Use</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-semibold text-lg mb-2">1. Necessary Cookies (Always Active)</h3>
              <p className="text-sm text-muted-foreground mb-3">
                These cookies are essential for the website to function properly and cannot be disabled.
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li><strong>cookie-consent</strong>: Stores your cookie preferences (Duration: 12 months)</li>
                <li><strong>Session cookies</strong>: Enable secure authentication and page navigation</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">2. Analytics Cookies</h3>
              <p className="text-sm text-muted-foreground mb-3">
                These cookies help us understand how visitors interact with our website.
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li><strong>Custom Analytics</strong>: Tracks page views, visit duration, scroll depth, and traffic sources</li>
                <li><strong>Data Collected</strong>: Page path, referrer, user agent, screen resolution, UTM parameters</li>
                <li><strong>Purpose</strong>: Improve website performance and user experience</li>
                <li><strong>Personal Data</strong>: Anonymized; no personally identifiable information collected</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">3. Advertising Cookies (Two-Tier System)</h3>
              <p className="text-sm text-muted-foreground mb-3">
                We use Google AdSense to display advertisements. You have control over the type of ads you see:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground ml-4">
                <li>
                  <strong>Personalized Ads (requires consent)</strong>: If you accept advertising cookies, 
                  we serve personalized ads tailored to your interests based on your browsing behavior. 
                  These ads use tracking cookies and are more relevant to you.
                </li>
                <li>
                  <strong>Non-Personalized Ads (GDPR-compliant, no consent needed)</strong>: If you decline 
                  advertising cookies, we serve contextual ads based only on the current page content and 
                  general location. These ads don't track you or collect personal data, making them 
                  GDPR-compliant without consent.
                </li>
              </ul>
              <p className="text-sm text-muted-foreground mt-3">
                <strong>Important:</strong> You will see advertisements either way, but declining personalized 
                advertising means ads won't be tailored to your personal interests. Learn more:{' '}
                <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Google Advertising Policies
                </a>
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">4. Affiliate Marketing Cookies</h3>
              <p className="text-sm text-muted-foreground mb-3">
                These cookies track affiliate referrals and commissions.
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li><strong>TravelPayouts</strong>: Tracks clicks and purchases through our affiliate links</li>
                <li><strong>Purpose</strong>: Earn commissions from travel bookings made through our links</li>
                <li><strong>Duration</strong>: Varies by affiliate program (typically 30-90 days)</li>
                <li><strong>Learn More</strong>: <a href="https://www.travelpayouts.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">TravelPayouts Privacy Policy</a></li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>How to Manage Cookies</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">On Our Website</h3>
              <p className="text-sm text-muted-foreground">
                You can manage your cookie preferences at any time by clicking the "Cookie Settings" link in the footer of our website. 
                This will allow you to enable or disable different categories of cookies.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-2">In Your Browser</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Most web browsers allow you to control cookies through their settings. Here's how to manage cookies in popular browsers:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Safari</a></li>
                <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Microsoft Edge</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Do Not Track</h3>
              <p className="text-sm text-muted-foreground">
                Our website respects the "Do Not Track" (DNT) browser setting. If you have DNT enabled, we will not track your analytics data, 
                even if you have consented to analytics cookies.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Cookie Duration</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              Your cookie consent preferences are stored for 12 months. After this period, you will be asked to review and confirm your preferences again.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Changes to This Policy</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              We may update this Cookie Policy from time to time to reflect changes in our practices or legal requirements. 
              When we make significant changes, we will update the "Last updated" date at the top of this page and may notify you through 
              the cookie consent banner.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Contact Us</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              If you have any questions about our use of cookies, please contact us at:{' '}
              <a href="mailto:info@trottingtheworld.com" className="text-primary hover:underline">
                info@trottingtheworld.com
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
